import { get, isEqual } from 'lodash'
import queryString from 'query-string'
import { createAction } from 'redux-actions'
import { push } from 'connected-react-router'

import { actionCreators as api } from 'reduxBits/api'
import { cleanQuery } from 'utils/parameters'
import { getCurrentSearch } from 'utils/adBack'

import * as selectors from './selectors'

const prefix = 'Classified:'
export const REQUEST_ADS = `${prefix} Send an async search request to server.`
export const RECEIVE_ADS = `${prefix} Update the state with the fetched search result.`
export const CLEAR_SHIPPING_ADS = `${prefix} Clear shipping search result`
export const REQUEST_SHIPPING_ADS = `${prefix} Send an async shipping search request to server.`
export const RECEIVE_SHIPPING_ADS = `${prefix} Update the state with the fetched shipping ads result.`
export const CLEAR_ADS = `${prefix} clear search results.`
export const REQUEST_STORES = `${prefix} request stores information.`
export const RECEIVE_STORES = `${prefix} recieve stores information.`
export const REQUEST_STORE = `${prefix} request store information.`
export const RECEIVE_STORE = `${prefix} recieve store information.`
export const REQUEST_AD = `${prefix} Send an async get-me-an-ad request to server.`
export const RECEIVE_AD = `${prefix} Update the state with the fetched ad data.`
export const CLEAR_AD = `${prefix} clear ad.`
export const UPDATE_AC_FEEDBACK = `${prefix} Update the ac feedback parameter.`
export const REQUEST_SEARCH_TOTAL = `${prefix} Fetch the search total of a query.`
export const RECEIVE_SEARCH_TOTAL = `${prefix} Update the store with the search total of the current search.`
export const SET_AD_REPLY_VISIBILITY = `${prefix} Set the visibility (shown/hidden) of the messaging form.`
export const SET_ADWATCH_EMAIL_VISIBILITY = `${prefix} Set the visibility (shown/hidden) of the adwatch email settings.`
export const REQUEST_RELATED_ADS = `${prefix} Send an async request to server for ads related to the current ad.`
export const RECEIVE_RELATED_ADS = `${prefix} Update the state with the fetched ads related to the current ad.`
export const RELATED_AD_CLICKED = `${prefix} Related ad clicked.`
export const NO_UPDATE = `${prefix} No update of ads.`
export const SHOW_FILTER = 'Show filter'
export const CANCEL_FILTER = 'Cancel filter'
export const REQUEST_SEND_MESSAGE_IMAGE_TYPE = `${prefix} Send an async get image type`
export const RECEIVE_SEND_MESSAGE_IMAGE_TYPE = `${prefix} Update the state with the fetched image type`
export const RECEIVE_SEND_MESSAGE_IMAGE_TYPE_ERROR = `${prefix} Received error when fetching image type`
export const AD_CLICKED = `${prefix} Ad clicked.`
export const CONTRACT_OF_SALE_CLICKED = `${prefix} Contract of sale clicked`
export const INFO_PAGE_LINK_CLICKED = `${prefix} Info page link clicked`
export const BLOCKET_PAYMENT_BUTTON_CLICKED = `${prefix} Blocket payment clicked`
export const BLOCKET_PAYMENT_BADGE_CLICKED = `${prefix} Blocket payment badge clicked`
export const BLOCKET_PAYMENT_CREDITSPACE_CLICKED = `${prefix} Blocket payment creditspace link clicked`

const requestAds = createAction(REQUEST_ADS)
const receiveAds = createAction(RECEIVE_ADS)
const clearShippingAds = createAction(CLEAR_SHIPPING_ADS)
const requestShippingAds = createAction(REQUEST_SHIPPING_ADS)
const receiveShippingAds = createAction(RECEIVE_SHIPPING_ADS)
const clearAds = createAction(CLEAR_ADS)
const requestStores = createAction(REQUEST_STORES)
const receiveStores = createAction(RECEIVE_STORES)
const requestStore = createAction(REQUEST_STORE)
const receiveStore = createAction(RECEIVE_STORE)
const requestAd = createAction(REQUEST_AD)
const receiveAd = createAction(RECEIVE_AD)
const clearAd = createAction(CLEAR_AD)
const relatedAdClicked = createAction(RELATED_AD_CLICKED)

const updateAcFeedback = createAction(UPDATE_AC_FEEDBACK)
const requestSearchTotal = createAction(REQUEST_SEARCH_TOTAL)
const receiveSearchTotal = createAction(RECEIVE_SEARCH_TOTAL)
const setShowAdReply = createAction(
  SET_AD_REPLY_VISIBILITY,
  (payload) => payload,
  (payload, meta) => ({ byAction: meta }),
)
const showAdWatchEmail = createAction(SET_ADWATCH_EMAIL_VISIBILITY)

const noUpdate = createAction(NO_UPDATE)
const showFilter = createAction(SHOW_FILTER)
const cancelFilter = createAction(CANCEL_FILTER)
const requestSendMessageImageType = createAction(REQUEST_SEND_MESSAGE_IMAGE_TYPE)
const receiveSendMessageImageType = createAction(RECEIVE_SEND_MESSAGE_IMAGE_TYPE)
const receiveSendMessageImageTypeError = createAction(RECEIVE_SEND_MESSAGE_IMAGE_TYPE_ERROR)
const adClicked = createAction(AD_CLICKED)
const contractOfSaleClicked = createAction(CONTRACT_OF_SALE_CLICKED)
const infoPageLinkClicked = createAction(INFO_PAGE_LINK_CLICKED)
const blocketPaymentButtonClicked = createAction(BLOCKET_PAYMENT_BUTTON_CLICKED)
const blocketPaymentBadgeClicked = createAction(BLOCKET_PAYMENT_BADGE_CLICKED)
const blocketPaymentCreditSpaceClicked = createAction(BLOCKET_PAYMENT_CREDITSPACE_CLICKED)

const getLocation = (getState) => getState().router.location

const fetchSearchTotal = (userQuery) => async (dispatch, getState) => {
  const query = {
    ...{ st: get(getState(), 'searchParams.byQueryKey.st.defaultValue') },
    ...cleanQuery(userQuery, getState()),
  }
  // An optimization, the API won't send us ad data when we're only interested in the total.
  query.lim = '0'
  dispatch(requestSearchTotal())

  const response = await dispatch(
    api.fetch(`search_bff/v1/content?${queryString.stringify(query)}`),
  )

  if (response.ok) {
    return dispatch(
      receiveSearchTotal({
        searchTotal: response.textOrJson.totalCount,
      }),
    )
  }

  return dispatch(receiveSearchTotal(response))
}

const fetchStores = (searchQuery) => async (dispatch, getState) => {
  const state = getState()
  const query = queryString.stringify({
    lim: selectors.numAdsPerPage(state),
    ...cleanQuery(searchQuery, state),
    ...(searchQuery.page && { page: searchQuery.page - 1 }),
  })
  dispatch(requestStores())

  const response = await dispatch(api.fetch(`search_bff/v1/stores?${query}`))
  if (response.ok) {
    const result = response.textOrJson

    return dispatch(
      receiveStores({
        stores: result.data,
        totalPageCount: result.totalPageCount,
        totalCount: result.totalCount,
      }),
    )
  }

  return dispatch(receiveStores(response))
}

const fetchStore = (storeName) => async (dispatch) => {
  dispatch(requestStore())

  // Might stop some kind of injection, haven't succeeded in doing anything malicious myself though
  const encodedStoreName = encodeURIComponent(storeName)

  try {
    const response = await dispatch(api.fetch(`search_bff/v1/stores?name=${encodedStoreName}`))

    dispatch(receiveStore(get(response, 'textOrJson.data[0]')))
  } catch (error) {
    dispatch(receiveStore(error))
  }
}

const fetchShippingAds = (searchQuery, checkNewAds = false) => async (dispatch, getState) => {
  const state = getState()

  const isError = state.classified.shippingAds.error

  const query = {
    ...searchQuery,
    lim: 3,
    page: 1,
    invert_geo: 1,
  }

  const noUpdates = !isError && isEqual(query, state.classified.shippingAds.query)
  if (noUpdates && !checkNewAds) {
    return dispatch(noUpdate('fetchShippingAds'))
  }

  dispatch(requestShippingAds(query))

  const response = await dispatch(
    api.fetch(`search_bff/v1/content?${queryString.stringify(query)}&sh=1`),
  )

  if (response.ok) {
    const result = response.textOrJson

    return dispatch(
      receiveShippingAds({
        ads: result.data,
        category: result.searchSubsetDescription,
        nextScrollId: result.nextScrollId,
        totalShippingAdsCount: result.totalCount,
        query,
      }),
    )
  }

  return dispatch(receiveShippingAds(response))
}

const fetchAds = (searchQuery, checkNewAds = false) => async (dispatch, getState) => {
  const state = getState()

  const { acFeedback } = state.classified
  const isError = state.classified.searchResults.error

  const query = {
    ...{ st: get(state, 'searchParams.byQueryKey.st.defaultValue') },
    lim: selectors.numAdsPerPage(state),
    ...cleanQuery(searchQuery, state),
    ...(acFeedback && { ac_feedback: acFeedback }),
    ...(searchQuery.page && { page: searchQuery.page - 1 }), // api is 0-index based
    ...(searchQuery.storeId && { store_id: searchQuery.storeId }),
  }

  const noUpdates = !isError && isEqual(query, state.classified.searchResults.query)
  if (noUpdates && !checkNewAds) {
    return dispatch(noUpdate('fetchAds'))
  }

  dispatch(requestAds(query))
  dispatch(clearShippingAds())

  const response = await dispatch(
    api.fetch(`search_bff/v1/content?${queryString.stringify(query)}&include=all&gl=3`),
  )

  if (response.ok) {
    const result = response.textOrJson

    if (
      (Number(searchQuery.page) === result.totalPageCount || result.totalPageCount <= 1) &&
      Boolean(searchQuery.r) &&
      get(state, 'plugins.features.extended-shipping.enabled', false)
    ) {
      await dispatch(fetchShippingAds(searchQuery))
    }
    return dispatch(
      receiveAds({
        nextScrollId: result.nextScrollId,
        ads: result.data,
        inventory: get(result, '_raw.inventory', {}),
        searchTotal: result.totalCount,
        totalPageCount: result.totalPageCount,
        querySignature: result.querySignature,
        searchSaved: result.searchSaved,
        adwatchId: result.adwatchId,
        searchTitle: result.title,
        searchSubsetDescription: result.searchSubsetDescription,
        gallery: result.gallery || [],
        query,
      }),
    )
  }

  return dispatch(receiveAds(response))
}

const fetchAd = (id) => async (dispatch) => {
  dispatch(requestAd())

  const options = {
    include: ['safety_tips', 'store', 'partner_placements', 'breadcrumbs', 'archived'],
    status: ['active', 'deleted', 'hidden_by_user'],
  }

  const response = await dispatch(
    api.fetch(`search_bff/v1/content/${id}?${queryString.stringify(options)}`),
  )

  if (response.ok) {
    return dispatch(
      receiveAd({
        ...response.textOrJson,
      }),
    )
  }

  return dispatch(receiveAd(response))
}

const showAdReply = (payload, meta) => (dispatch) => {
  if (!payload) {
    // Remove querystring params on close
    window.history.replaceState(null, 'Open Reply Modal', window.location.href.split('?')[0])
  }

  dispatch(setShowAdReply(payload, meta))
}

const goBack = () => (dispatch) => {
  dispatch(cancelFilter())

  const currentSearch = getCurrentSearch()

  if (currentSearch && currentSearch.location) {
    dispatch(push(currentSearch.location))
    return
  }

  dispatch(push('/annonser'))
}

const getSendMessageImageType = (width, height, scaleMode) => async (dispatch) => {
  dispatch(requestSendMessageImageType())

  const response = await dispatch(
    api.fetch(`classifieds/v1/image_types?width=${width}&height=${height}&scale_mode=${scaleMode}`),
  )

  if (response.ok) {
    return dispatch(receiveSendMessageImageType(response.textOrJson))
  }

  return dispatch(receiveSendMessageImageTypeError(response))
}

const clickedShowFilter = () => (dispatch, getState) => {
  const location = getLocation(getState)
  dispatch(
    push({
      search: location.search,
      pathname: '/filter',
    }),
  )

  dispatch(showFilter())
}

export const actionCreators = {
  adClicked,
  blocketPaymentBadgeClicked,
  blocketPaymentButtonClicked,
  blocketPaymentCreditSpaceClicked,
  clearAds,
  clearShippingAds,
  clickedShowFilter,
  contractOfSaleClicked,
  fetchAd,
  fetchAds,
  fetchSearchTotal,
  fetchShippingAds,
  fetchStore,
  fetchStores,
  getSendMessageImageType,
  goBack,
  infoPageLinkClicked,
  noUpdate,
  receiveAd,
  clearAd,
  receiveAds,
  receiveShippingAds,
  relatedAdClicked,
  showAdReply,
  showAdWatchEmail,
  updateAcFeedback,
}
